// <copyright file="batch-header-detail.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { html, nothing, TemplateResult } from 'lit';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { serverDateTimeToLocal } from '../../../webmodule-common/other/datetime-converter';

export interface BatchHeaderDetailPair {
  label: string;
  value: string | null | undefined;
}

/**
 * Format a batch delivery date (server date or date-time string) for the inline group-header "fast view",
 * or null when unset. Date-only in the viewer's locale and zone — the header is a glance, the dialog has
 * the full detail. Uses the same `toLocaleDateString` presentation every other date column in the app does;
 * the RFC3339 helpers are input[type=date] value formatters, not display formatters.
 */
export function batchHeaderDate(value: string | null | undefined): string | null {
  if (isEmptyOrSpace(value ?? undefined)) return null;
  return serverDateTimeToLocal(value as string).toLocaleDateString();
}

/**
 * Inline "fast view" of a delivery batch's key values on a group-header row: one badge per value, each
 * rendered only when its value is set. Shared by the quote item table and the purchase-order item table so
 * both read identically.
 *
 * Rendered as badges rather than muted text so the delivery date / customer reference carry the same visual
 * weight as the item-count badge beside them — this is the information the batch exists for, and as small
 * grey text it disappeared into the header. Rendered inside the table's native group-header cell (shadow
 * DOM), so the styling comes from the host table's customStyles (`.batch-header-detail`,
 * `.batch-header-badge`). Returns `nothing` when no pair has a value.
 */
export function renderBatchHeaderDetail(pairs: BatchHeaderDetailPair[]): TemplateResult | typeof nothing {
  const set = pairs.filter(p => !isEmptyOrSpace(p.value ?? undefined));
  if (set.length === 0) return nothing;
  return html`<span class="batch-header-detail">
    ${set.map(p => html`<span class="badge batch-header-badge">${p.label}: ${p.value}</span>`)}
  </span>`;
}
